/**
 * DarkAuthModal Component
 * 
 * Dark-themed authentication modal for landing2.
 * Supports login and signup modes with enhanced validation.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

// Password strength calculator
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  
  // Length checks
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  
  // Character variety
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  
  // Common patterns (reduce score)
  const commonPatterns = ['password', '123456', 'qwerty', 'abc123'];
  if (commonPatterns.some(p => password.toLowerCase().includes(p))) score = Math.max(0, score - 2);
  
  if (score <= 2) return { score, label: 'Weak', color: '#ef4444' };
  if (score <= 4) return { score, label: 'Fair', color: '#f59e0b' };
  if (score <= 5) return { score, label: 'Good', color: '#22c55e' };
  return { score, label: 'Strong', color: '#10b981' };
};

// Verification Pending Content Component
const VerificationPendingContent = ({ email, onResend, onClose }) => {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0) return;
    
    setResendLoading(true);
    setResendError('');
    setResendSuccess(false);
    
    try {
      const result = await onResend();
      if (result.success) {
        setResendSuccess(true);
        setCooldown(60); // 60 second cooldown
      } else {
        setResendError(result.error || 'Failed to resend. Please try again.');
      }
    } catch (err) {
      setResendError('An error occurred. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="dark-auth-modal__verification-pending">
      {/* Email Icon */}
      <div className="verification-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      
      {/* Title & Message */}
      <h2 className="verification-title">Check your email</h2>
      <p className="verification-message">
        We've sent a verification link to<br />
        <strong>{email}</strong>
      </p>
      
      {/* Instructions */}
      <div className="verification-instructions">
        <p>Click the link in the email to verify your account and complete registration.</p>
        <p className="hint">The link expires in 24 hours.</p>
      </div>
      
      {/* Resend Section */}
      <div className="verification-resend">
        <p>Didn't receive the email?</p>
        
        {resendSuccess && (
          <div className="resend-success">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Email sent! Check your inbox.
          </div>
        )}
        
        {resendError && (
          <div className="resend-error">
            {resendError}
          </div>
        )}
        
        <button 
          className="resend-btn"
          onClick={handleResend}
          disabled={resendLoading || cooldown > 0}
        >
          {resendLoading ? (
            <>
              <span className="dark-auth-modal__spinner" />
              Sending...
            </>
          ) : cooldown > 0 ? (
            `Resend in ${cooldown}s`
          ) : (
            'Resend verification email'
          )}
        </button>
      </div>
      
      {/* Continue Button */}
      <button 
        className="dark-auth-modal__submit"
        onClick={onClose}
        style={{ marginTop: '24px' }}
      >
        Continue to Dashboard
      </button>
      
      {/* Help Text */}
      <p className="verification-help">
        Check your spam folder if you don't see the email.
      </p>
    </div>
  );
};

const DarkAuthModal = ({ isOpen, onClose, initialMode = 'login', onGuestContinue }) => {
  const [mode, setMode] = useState(initialMode);
  const [verificationPending, setVerificationPending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    roles: ['Instructor'] // Landing page is for tutors only
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showSocialNotice, setShowSocialNotice] = useState(false);
  const { login, register, resendVerificationEmail } = useAuth();

  // Calculate password strength
  const passwordStrength = useMemo(() => 
    calculatePasswordStrength(formData.password), 
    [formData.password]
  );

  useEffect(() => {
    setMode(initialMode);
    setFormData({ email: '', password: '', confirmPassword: '', name: '', roles: ['Instructor'] });
    setFieldErrors({});
    setError('');
    setAgreedToTerms(false);
    setShowSocialNotice(false);
    setVerificationPending(false);
  }, [initialMode, isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear field-specific error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
    setError('');
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => {
      const currentRoles = prev.roles;
      if (currentRoles.includes(role)) {
        // Don't allow removing all roles
        if (currentRoles.length === 1) return prev;
        return { ...prev, roles: currentRoles.filter(r => r !== role) };
      } else {
        return { ...prev, roles: [...currentRoles, role] };
      }
    });
  };

  const validateForm = () => {
    const errors = {};
    
    if (mode === 'signup') {
      // Name validation
      if (!formData.name.trim()) {
        errors.name = 'Name is required';
      } else if (formData.name.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters';
      }
      
      // Password confirmation
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
      
      // Password strength
      if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
      
      // Terms agreement
      if (!agreedToTerms) {
        errors.terms = 'You must agree to the terms';
      }
    }
    
    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    // Password required
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      if (mode === 'login') {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          onClose();
        } else {
          // Parse field-specific errors from backend
          if (typeof result.error === 'object') {
            setFieldErrors(result.error);
          } else {
            setError(typeof result.error === 'string' ? result.error : 'Login failed. Please check your credentials.');
          }
        }
      } else {
        // Generate username from email
        const username = formData.email.split('@')[0] + Math.floor(Math.random() * 1000);
        const registrationData = {
          email: formData.email,
          password: formData.password,
          name: formData.name.trim(),
          username: username,
          roles: formData.roles
        };
        const result = await register(registrationData);
        if (result.success) {
          // Show verification pending state
          if (result.emailVerificationSent) {
            setVerificationPending(true);
          } else {
            onClose();
          }
        } else {
          // Parse field-specific errors from backend
          if (typeof result.error === 'object') {
            setFieldErrors(result.error);
          } else {
            setError(typeof result.error === 'string' ? result.error : 'Registration failed. Please try again.');
          }
        }
      }
    } catch (err) {
      setError('An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialClick = () => {
    setShowSocialNotice(true);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setFieldErrors({});
    setError('');
    setShowSocialNotice(false);
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { type: 'spring', damping: 25, stiffness: 300 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95, 
      y: 20,
      transition: { duration: 0.2 }
    },
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="dark-auth-modal__overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div
            className="dark-auth-modal__content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button className="dark-auth-modal__close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Verification Pending State */}
            {verificationPending ? (
              <VerificationPendingContent 
                email={formData.email} 
                onResend={async () => {
                  const result = await resendVerificationEmail();
                  return result;
                }}
                onClose={onClose}
              />
            ) : (
              <>
            {/* Header */}
            <div className="dark-auth-modal__header">
              <h2 className="dark-auth-modal__title">
                {mode === 'login' ? 'Welcome back' : 'Join as an Educator'}
              </h2>
              <p className="dark-auth-modal__subtitle">
                {mode === 'login' 
                  ? 'Sign in to link your submission to your profile.'
                  : 'Create your educator account to track your position and get early access.'
                }
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="dark-auth-modal__form">
              {mode === 'signup' && (
                <div className="dark-auth-modal__group">
                  <label className="dark-auth-modal__label">Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    className={`dark-auth-modal__input ${fieldErrors.name ? 'input-error' : ''}`}
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                  />
                  {fieldErrors.name && (
                    <span className="dark-auth-modal__field-error">{fieldErrors.name}</span>
                  )}
                </div>
              )}

              <div className="dark-auth-modal__group">
                <label className="dark-auth-modal__label">Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  className={`dark-auth-modal__input ${fieldErrors.email ? 'input-error' : ''}`}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                />
                {fieldErrors.email && (
                  <span className="dark-auth-modal__field-error">{fieldErrors.email}</span>
                )}
              </div>

              <div className="dark-auth-modal__group">
                <label className="dark-auth-modal__label">Password <span className="required">*</span></label>
                <input
                  type="password"
                  name="password"
                  className={`dark-auth-modal__input ${fieldErrors.password ? 'input-error' : ''}`}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                />
                {fieldErrors.password && (
                  <span className="dark-auth-modal__field-error">{fieldErrors.password}</span>
                )}
                
                {/* Password Strength Meter - Only show in signup mode */}
                {mode === 'signup' && formData.password && (
                  <div className="dark-auth-modal__password-strength">
                    <div className="strength-bar">
                      <div 
                        className="strength-fill" 
                        style={{ 
                          width: `${(passwordStrength.score / 6) * 100}%`,
                          backgroundColor: passwordStrength.color 
                        }} 
                      />
                    </div>
                    <span className="strength-label" style={{ color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
                
                {mode === 'signup' && (
                  <span className="dark-auth-modal__hint">
                    At least 8 characters with mixed case and numbers
                  </span>
                )}
              </div>

              {/* Confirm Password - Only in signup mode */}
              {mode === 'signup' && (
                <div className="dark-auth-modal__group">
                  <label className="dark-auth-modal__label">Confirm Password <span className="required">*</span></label>
                  <input
                    type="password"
                    name="confirmPassword"
                    className={`dark-auth-modal__input ${fieldErrors.confirmPassword ? 'input-error' : ''}`}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                  />
                  {fieldErrors.confirmPassword && (
                    <span className="dark-auth-modal__field-error">{fieldErrors.confirmPassword}</span>
                  )}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && (
                    <span className="dark-auth-modal__match-success">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Passwords match
                    </span>
                  )}
                </div>
              )}

              {/* Terms Agreement - Only in signup mode */}
              {mode === 'signup' && (
                <div className="dark-auth-modal__group">
                  <label className={`dark-auth-modal__checkbox-label ${fieldErrors.terms ? 'error' : ''}`}>
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => {
                        setAgreedToTerms(e.target.checked);
                        if (fieldErrors.terms) {
                          setFieldErrors({ ...fieldErrors, terms: '' });
                        }
                      }}
                    />
                    <span className="checkbox-custom" />
                    <span className="checkbox-text">
                      I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    </span>
                  </label>
                  {fieldErrors.terms && (
                    <span className="dark-auth-modal__field-error">{fieldErrors.terms}</span>
                  )}
                </div>
              )}

              {error && (
                <div className="dark-auth-modal__error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="dark-auth-modal__submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="dark-auth-modal__spinner" />
                    Processing...
                  </>
                ) : (
                  mode === 'login' ? 'Sign In' : 'Create Account'
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="dark-auth-modal__divider">
              <span>or continue with</span>
            </div>

            {/* Social Buttons */}
            <div className="dark-auth-modal__social">
              <button 
                className="dark-auth-modal__social-btn"
                onClick={handleSocialClick}
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>
              <button 
                className="dark-auth-modal__social-btn"
                onClick={handleSocialClick}
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Apple
              </button>
            </div>

            {/* Social Auth Notice */}
            <AnimatePresence>
              {showSocialNotice && (
                <motion.div 
                  className="dark-auth-modal__social-notice"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="notice-content">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <div className="notice-text">
                      <strong>Social login coming soon!</strong>
                      <p>For now, please create an account with us — it's simple and secure. Or continue as a guest to register your interest.</p>
                    </div>
                  </div>
                  <div className="notice-actions">
                    {onGuestContinue && (
                      <button 
                        type="button" 
                        className="notice-btn notice-btn--ghost"
                        onClick={() => {
                          onClose();
                          onGuestContinue();
                        }}
                      >
                        Continue as Guest
                      </button>
                    )}
                    <button 
                      type="button" 
                      className="notice-btn notice-btn--primary"
                      onClick={() => {
                        setShowSocialNotice(false);
                        if (mode === 'login') switchMode('signup');
                      }}
                    >
                      {mode === 'login' ? 'Create Account' : 'Fill Form Above'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer */}
            <div className="dark-auth-modal__footer">
              {mode === 'login' ? (
                <>
                  Don't have an account?{' '}
                  <button 
                    className="dark-auth-modal__link" 
                    onClick={() => switchMode('signup')}
                    type="button"
                  >
                    Create one
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button 
                    className="dark-auth-modal__link" 
                    onClick={() => switchMode('login')}
                    type="button"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default DarkAuthModal;
